import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Lesson() {
  const { refresh } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("english");
  const [selected, setSelected] = useState(null);
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lesson/today");
        setLesson(r.data);
        if (r.data.quiz_answered) {
          setAnswer({
            correct: r.data.quiz_correct,
            correct_index: r.data.quiz?.correct_index,
            explanation: r.data.quiz?.explanation,
          });
        }
      } catch {
        toast.error("Failed to load lesson");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async () => {
    if (selected == null) return;
    try {
      const r = await api.post("/lesson/quiz/answer", {
        quiz_id: lesson.quiz.id,
        answer_index: selected,
      });
      setAnswer(r.data);
      if (r.data.correct) {
        toast.success(`+${r.data.xp_earned ?? 25} XP · Correct!`);
      } else {
        toast.info(`+${r.data.xp_earned ?? 5} XP · Learn from it`);
      }
      refresh();
    } catch {
      toast.error("Submit failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="text-cyan-400 mx-auto animate-pulse" size={32} />
          <div className="label-tech mt-3">Channeling wisdom...</div>
        </div>
      </div>
    );
  }
  if (!lesson) return null;

  const lines = lesson[lang] || [];

  return (
    <div className="space-y-6" data-testid="lesson-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tech">Daily Wisdom</div>
          <h1 className="h-display text-3xl sm:text-4xl mt-1">
            {lesson.title}
          </h1>
          <p className="text-sm text-slate-400 mt-1">10 lines · English + हिंदी</p>
        </div>
        <div className="flex border border-white/10">
          <button
            data-testid="lang-english"
            onClick={() => setLang("english")}
            className={`px-4 py-2 font-display uppercase text-xs tracking-widest ${
              lang === "english" ? "bg-cyan-400 text-black" : "text-slate-400 hover:text-white"
            }`}
          >
            English
          </button>
          <button
            data-testid="lang-hindi"
            onClick={() => setLang("hindi")}
            className={`px-4 py-2 font-display uppercase text-xs tracking-widest ${
              lang === "hindi" ? "bg-violet-500 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            हिंदी
          </button>
        </div>
      </div>

      <div className="panel panel-corners p-6">
        <ol className="space-y-3">
          {lines.map((line, i) => (
            <li key={i} className="flex gap-4 border-l-2 border-l-cyan-400/40 pl-4 py-1">
              <span className="font-mono-tech text-xs text-cyan-400 pt-1 w-8">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm sm:text-base text-slate-200 leading-relaxed">{line}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Quiz */}
      <div className="panel panel-corners p-6" data-testid="quiz-block">
        <div className="label-tech mb-3">Wisdom Trial</div>
        <p className="text-slate-200 mb-5 leading-relaxed">{lesson.quiz.situation}</p>
        <div className="space-y-2">
          {lesson.quiz.options.map((opt, i) => {
            const isSelected = selected === i;
            const showResult = !!answer;
            const isCorrect = showResult && i === answer.correct_index;
            const isWrong = showResult && isSelected && !isCorrect;
            return (
              <button
                key={i}
                data-testid={`quiz-option-${i}`}
                disabled={showResult}
                onClick={() => setSelected(i)}
                className={`w-full text-left border p-3 flex items-center gap-3 transition ${
                  isCorrect
                    ? "border-emerald-400 bg-emerald-500/10"
                    : isWrong
                    ? "border-red-400 bg-red-500/10"
                    : isSelected
                    ? "border-cyan-400 bg-cyan-500/10"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
                <span className="font-mono-tech text-xs text-cyan-400">{String.fromCharCode(65 + i)}</span>
                <span className="text-sm flex-1">{opt}</span>
                {isCorrect && <Check size={16} className="text-emerald-400" />}
                {isWrong && <X size={16} className="text-red-400" />}
              </button>
            );
          })}
        </div>

        {!answer && (
          <button
            data-testid="quiz-submit"
            disabled={selected == null}
            onClick={submit}
            className="btn-neon mt-4"
          >
            Submit Answer
          </button>
        )}

        {answer && (
          <div
            data-testid="quiz-result"
            className={`mt-4 p-4 border ${
              answer.correct ? "border-emerald-400/50 bg-emerald-500/5" : "border-amber-400/50 bg-amber-500/5"
            }`}
          >
            <div className="label-tech mb-1">
              {answer.correct ? "System — Correct" : "System — Reflect"}
            </div>
            <p className="text-sm text-slate-200">{answer.explanation}</p>
          </div>
        )}
      </div>
    </div>
  );
}
